import { TableDetailsTabType, getWebPath } from '@doss/common-all'
import { MoreVertRounded } from '@mui/icons-material'
import { Box, Stack, Theme, Typography } from '@mui/material'
import {
  GridColumnVisibilityModel,
  GridRowSelectionModel,
  GridSortModel,
} from '@mui/x-data-grid-premium'
import { clone } from 'lodash'
import { ChevronsRight } from 'lucide-react'
import { Dispatch, SetStateAction, SyntheticEvent, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { cn } from '@/lib/utils'
import { useAnchorRef } from '~/common/hooks/useAnchorRef'
import { useApplicationOrientation } from '~/common/hooks/useApplicationOrientation'
import { useDeviceType } from '~/common/hooks/useDeviceQuery'
import { useGlobalContext } from '~/common/hooks/useGlobalContext'
import useStyles from '~/common/hooks/useStyles'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { getHasChangedFilters } from '~/common/utils/table'
import { DossIcon } from '~/components/DossIcon'
import { DossGridFilterModel, filterOperatorToBooleanOperator } from '~/components/tables/filters'
import {
  DetailedTable,
  DetailedTableColumnWithTable,
  DetailedTableRow,
  DetailedTableTab,
  TableType,
} from '~/components/tables/types'
import { IconButton } from '~/components/ui/atoms/icon-button/IconButton'
import { Tooltip } from '~/components/ui/atoms/tooltip/Tooltip'
import {
  ApplicationOrientation,
  BreadcrumbObjectType,
  TableFilterInput,
} from '../../../common/generated/apollo-operations'
import { useTableDetails } from '../../tables/TableDetailsProvider'
import { TableSource } from '../DossTable'
import { ToggleableSearchField } from '../ToggleableSearchField'
import { BulkActionsMenu } from './BulkActionsMenu'
import { DossTableLevelMenu } from './DossTableLevelMenu'
import { TableTabButtons } from './TableTabButtons'
import { TableTabs } from './TableTabs'

export interface TableToolbarProps {
  table: DetailedTable
  title: string
  tableSource: TableSource
  searchString?: string
  onSearch: (newSearchString?: string) => void
  type: TableType
  orderedTableTabs: DetailedTableTab[]
  selectedTab: DetailedTableTab | null
  setOrderedTableTabs: (tableTabs: DetailedTableTab[]) => void
  handleTabChange: (tab: DetailedTableTab) => void
  filterModel: DossGridFilterModel
  sortModel: GridSortModel
  tableColumns: DetailedTableColumnWithTable[]
  columnVisibilityModel: GridColumnVisibilityModel
  savedColumnVisibilityModel?: GridColumnVisibilityModel
  onTableTabColumnsSaved?: (visibilityModel: GridColumnVisibilityModel) => void
  handleSortChange: (sortModel: GridSortModel) => void
  resetSelectedTab: () => void
  tabCountLimit: number
  hideTabButtons?: boolean
  readOnly: boolean
  tableRowsLoading?: boolean
  // Bulk actions props
  rowSelectionModel?: GridRowSelectionModel
  isSelectAll?: boolean
  totalRowCount?: number
  loadedRowCount?: number
  currentFilters?: TableFilterInput[]
  onClearSelection?: () => void
  setRowsData: Dispatch<SetStateAction<DetailedTableRow[]>>
}

function getStyles(theme: Theme) {
  return {
    childToolbar: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
      height: theme.spacing(5),
      paddingTop: theme.spacing(2),
      paddingInlineEnd: theme.spacing(1),
      paddingBottom: theme.spacing(2),
      paddingInlineStart: theme.spacing(2),
      gap: theme.spacing(2),
      borderRadius: theme.spacing(0.25, 0.25, 0, 0),
      backgroundColor: theme.palette.common.white,
      fontColor: theme.palette.text.primary,
      fontSize: '12px',
      popperZIndex: {
        zIndex: 999,
      },
    },
    toolbarContainer: {
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      height: 48,
      minHeight: 48,
      paddingBlock: theme.spacing(0),
      paddingInline: theme.spacing(1.5),
      borderRadius: theme.spacing(0.25, 0.25, 0, 0),
      width: '100%',
    },
    tableTitle: {
      paddingInlineEnd: theme.spacing(1),
      minWidth: 150,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      '&:hover': {
        textDecoration: 'underline',
        cursor: 'pointer',
      },
    },
    tabsStackContainer: {
      height: '56px',
    },
    actionsContainer: {
      marginInlineStart: theme.spacing(1),
    },
    tabsContainer: {
      flexGrow: 1,
      alignItems: 'center',
    },
    buttonContainer: {
      marginInlineStart: theme.spacing(1),
      justifyContent: 'flex-end',
      alignItems: 'center',
    },
    spacer: {
      flexGrow: 2,
    },
  }
}

export const TableToolbar = ({
  table,
  title,
  searchString,
  tableSource,
  onSearch,
  type,
  orderedTableTabs,
  filterModel,
  sortModel,
  tableColumns,
  selectedTab,
  setOrderedTableTabs,
  handleTabChange,
  columnVisibilityModel,
  savedColumnVisibilityModel,
  onTableTabColumnsSaved,
  resetSelectedTab,
  tabCountLimit,
  readOnly,
  // On the table detail page, child tables shouldn't have tab buttons because you can have multiple
  // detail panels open which would mean multiple toolbars modifying the same table causing bugs
  hideTabButtons = tableSource === TableSource.CHILD_IN_TABLE_DETAIL,
  // Bulk actions props
  rowSelectionModel,
  isSelectAll,
  totalRowCount,
  currentFilters,
  onClearSelection,
  setRowsData,
}: TableToolbarProps) => {
  const { t } = useTypedTranslation()
  const { orientation } = useApplicationOrientation()
  const showTitle =
    tableSource === TableSource.TABLE_IN_PROJECT_DETAIL ||
    tableSource === TableSource.CHILD_IN_TABLE_DETAIL
  const styles = useStyles(getStyles)
  const location = useLocation()
  const navigate = useNavigate()
  const { setNavigateToTabAfterCreation } = useTableDetails()
  const { permissionsManager } = useGlobalContext()
  const { isMobile } = useDeviceType()
  // On mobile, an expanded search takes over the row from the tab buttons
  const [searchExpanded, setSearchExpanded] = useState(false)
  const showTabButtons = !isMobile || !searchExpanded

  const anchorRef = useRef<HTMLDivElement>(null)
  const { isOpen, close, open, anchorEl } = useAnchorRef({ anchorRef })

  const tableId = table.id

  const hasUnsavedFilters = selectedTab
    ? getHasChangedFilters(tableColumns, selectedTab, filterModel, tableId, permissionsManager)
    : false

  const handleNavigate = () => {
    // We only want to navigate if they were not already on the table page
    // This happens on the project page or whenever the table appears on a page that is not the table page
    const asPath = location.pathname + location.search + location.hash
    if (!asPath.includes(`/table/${table.id}`)) {
      navigate(
        getWebPath({
          objectId: table.id,
          objectType: BreadcrumbObjectType.TABLE,
          tableParams: { tab: TableDetailsTabType.TABLE_TABS },
        }),
      )
    }
  }

  const handleTableLevelMenuToggle = (event: Event | SyntheticEvent) => {
    event.stopPropagation()
    if (isOpen) {
      close()
    } else {
      open()
    }
  }

  const handleTableLevelMenuClose = (e: Event | SyntheticEvent) => {
    e.stopPropagation()
    close()
  }

  const setOrderedTablesAfterTabCreation = (newTab: DetailedTableTab) => {
    // If exceeding tabCountLimit, set table tab at last visible index on top of table
    // Otherwise, set table tab at last index
    const updatedTabs = clone(orderedTableTabs)
    if (updatedTabs.length >= tabCountLimit) {
      updatedTabs.splice(tabCountLimit - 1, 0, newTab)
    } else {
      updatedTabs.push(newTab)
    }
    setOrderedTableTabs(updatedTabs)
    setNavigateToTabAfterCreation(newTab.id)
  }

  const renderContent = () => {
    // Check if it's a child table and render the toolbar accordingly
    if (
      type === TableType.CHILD_TABLE ||
      type === TableType.CHART ||
      type === TableType.BACKLINK_TABLE
    ) {
      return (
        <Stack direction="row" sx={styles.childToolbar}>
          {showTitle && (
            <div className="flex items-center gap-1">
              {table.icon && <DossIcon name={table.icon.name} size={16} />}
              <Typography>{title}</Typography>
            </div>
          )}
          {showTabButtons && <Stack direction="row" sx={styles.spacer} />}
          {showTabButtons && !hideTabButtons && selectedTab && (
            <TableTabButtons
              columnVisibilityModel={columnVisibilityModel}
              filterModel={filterModel}
              handleClose={handleTableLevelMenuClose}
              onTableTabColumnsSaved={onTableTabColumnsSaved}
              readOnly={readOnly}
              resetSelectedTab={resetSelectedTab}
              savedColumnVisibilityModel={savedColumnVisibilityModel}
              selectedTab={selectedTab}
              setOrderedTablesAfterTabCreation={setOrderedTablesAfterTabCreation}
              sortModel={sortModel}
              tableColumns={tableColumns}
              tableId={tableId}
              tableName={title}
              tableSource={tableSource}
              tableType={type}
            />
          )}
          <ToggleableSearchField
            clearSearchString
            onExpandedChange={setSearchExpanded}
            onSearch={onSearch}
            searchString={searchString}
            tableName={title}
          />
          <Box ref={anchorRef}>
            <IconButton
              onClick={handleTableLevelMenuToggle}
              size={'small'}
              type="button"
              variant={'ghost'}
            >
              <MoreVertRounded fontSize="small" sx={{ color: 'primary.main' }} />
            </IconButton>
          </Box>
          <DossTableLevelMenu
            anchorEl={anchorEl}
            handleClose={handleTableLevelMenuClose}
            open={isOpen}
            readOnly={readOnly}
            table={table}
          />
        </Stack>
      )
    }

    const hasSelectedRows = rowSelectionModel && rowSelectionModel.length > 0
    // Hide tabs when rows are selected AND we're not showing tab buttons (sidebar collapsed)
    const shouldHideTabs = hasSelectedRows && hideTabButtons

    return (
      <Stack className="group/tabs" direction="row" sx={styles.toolbarContainer}>
        {showTitle && (
          <Tooltip
            arrow
            className="table-title"
            id="tooltip-table-title"
            placement="top"
            title={t('table.open_tooltip', { tableName: title })}
          >
            <div
              className="group/title flex cursor-pointer flex-row items-center gap-2"
              dir={orientation === ApplicationOrientation.RIGHT_TO_LEFT ? 'rtl' : 'ltr'}
              onClick={!readOnly ? handleNavigate : undefined}
            >
              {table.icon && <DossIcon name={table.icon.name} size={20} />}
              <Typography className="truncate text-nowrap group-hover/title:underline" variant="h6">
                {title}
              </Typography>
            </div>
          </Tooltip>
        )}

        {/* Bulk Actions Menu - shown when rows are selected and feature is enabled */}
        {hasSelectedRows && (
          <div className="ms-2 flex items-center">
            <BulkActionsMenu
              currentFilterBooleanOperator={filterOperatorToBooleanOperator(
                filterModel.logicOperator,
              )}
              currentFilters={currentFilters}
              hasUnsavedFilters={hasUnsavedFilters}
              isSelectAll={isSelectAll ?? false}
              onClearSelection={onClearSelection}
              rowSelectionModel={rowSelectionModel}
              searchString={searchString}
              setRowsData={setRowsData}
              table={table}
              tableId={tableId}
              tableTabId={selectedTab?.id}
              totalRowCount={totalRowCount ?? 0}
            />
          </div>
        )}

        {/* Table Tabs - hidden when rows selected and sidebar collapsed */}
        {!shouldHideTabs && (
          <Stack className="table-toolbar-actions" direction="row" sx={styles.tabsStackContainer}>
            <TableTabs
              handleTabChange={handleTabChange}
              hideTabButtons={hideTabButtons}
              orderedTableTabs={orderedTableTabs}
              readOnly={readOnly}
              selectedTab={selectedTab}
              setOrderedTableTabs={setOrderedTableTabs}
              setOrderedTablesAfterTabCreation={setOrderedTablesAfterTabCreation}
              tabCountLimit={tabCountLimit}
              tableColumns={tableColumns}
              tableId={tableId}
              tableName={title}
            />
          </Stack>
        )}
        <Stack direction="row" sx={styles.spacer} />
        <Stack
          className="table-toolbar-actions"
          direction="row"
          spacing={1}
          sx={styles.buttonContainer}
        >
          {showTabButtons && selectedTab && (
            <TableTabButtons
              columnVisibilityModel={columnVisibilityModel}
              filterModel={filterModel}
              handleClose={handleTableLevelMenuClose}
              onTableTabColumnsSaved={onTableTabColumnsSaved}
              readOnly={readOnly}
              resetSelectedTab={resetSelectedTab}
              savedColumnVisibilityModel={savedColumnVisibilityModel}
              selectedTab={selectedTab}
              setOrderedTablesAfterTabCreation={setOrderedTablesAfterTabCreation}
              sortModel={sortModel}
              tableColumns={tableColumns}
              tableId={tableId}
              tableName={title}
              tableSource={tableSource}
              tableType={type}
            />
          )}
          <ToggleableSearchField
            clearSearchString
            onExpandedChange={setSearchExpanded}
            onSearch={onSearch}
            searchString={searchString}
            tableName={title}
          />
          <Box ref={anchorRef}>
            <IconButton
              onClick={handleTableLevelMenuToggle}
              size={'small'}
              type="button"
              variant={'ghost'}
            >
              <MoreVertRounded />
            </IconButton>
          </Box>
        </Stack>
        <DossTableLevelMenu
          anchorEl={anchorEl}
          handleClose={handleTableLevelMenuClose}
          open={isOpen}
          readOnly={readOnly}
          table={table}
        />
      </Stack>
    )
  }

  return <>{renderContent()}</>
}

export const CollapseTableTabNavButton = ({
  tableTabNavOpen = false,
  setTableTabNavOpen,
  hideTabButtons = false,
  hideTableExpander = false,
}: {
  tableTabNavOpen?: boolean
  setTableTabNavOpen?: (open: boolean) => void
  hideTabButtons?: boolean
  hideTableExpander?: boolean
}) => {
  return (
    <div
      className={cn(
        `item-center absolute start-1 w-6 cursor-pointer text-gray-8 opacity-0 transition duration-300`,
        !hideTabButtons && 'group-hover/dtable:opacity-100',
        (tableTabNavOpen || hideTableExpander) && 'hidden',
      )}
      onClick={(e) => {
        e.stopPropagation()
        setTableTabNavOpen?.(true)
      }}
    >
      <ChevronsRight size={12} />
    </div>
  )
}
